import { useRef, type KeyboardEvent } from "react";
import type { MembershipCategory } from "../../api/members";

interface MemberFiltersProps {
  search: string;
  category: MembershipCategory | "";
  onSearchChange: (value: string) => void;
  onCategoryChange: (value: MembershipCategory | "") => void;
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="10.8" cy="10.8" r="5.8" stroke="currentColor" strokeWidth="1.8" />
      <path d="m15.2 15.2 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

const CATEGORY_OPTIONS: { value: MembershipCategory | ""; label: string }[] = [
  { value: "", label: "Todas" },
  { value: "colaborador", label: "Colaborador" },
  { value: "efetivo", label: "Efetivo" },
];

export function MemberFilters({ search, category, onSearchChange, onCategoryChange }: MemberFiltersProps) {
  const pillRefs = useRef<Array<HTMLButtonElement | null>>([]);

  function focusPill(index: number) {
    const target = pillRefs.current[index];
    target?.focus();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault();
      const next = (index + 1) % CATEGORY_OPTIONS.length;
      onCategoryChange(CATEGORY_OPTIONS[next].value);
      focusPill(next);
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault();
      const previous = (index - 1 + CATEGORY_OPTIONS.length) % CATEGORY_OPTIONS.length;
      onCategoryChange(CATEGORY_OPTIONS[previous].value);
      focusPill(previous);
    }
  }

  return (
    <section className="members-filters" aria-label="Filtros de associados">
      <div className="members-filters__field members-filters__field--search">
        <label htmlFor="member-search">Pesquisar associados</label>
        <div className="members-filters__search-control">
          <SearchIcon />
          <input
            id="member-search"
            type="search"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Nome ou usuário"
            autoComplete="off"
            aria-describedby="member-search-help"
          />
        </div>
        <p id="member-search-help">A pesquisa considera somente nome e usuário ativo.</p>
      </div>
      <div className="members-filters__field">
        <span className="members-filters__pills-label" id="member-category-label">Categoria</span>
        <div className="members-filters__pills" role="radiogroup" aria-labelledby="member-category-label">
          {CATEGORY_OPTIONS.map((option, index) => {
            const selected = option.value === category;
            return (
              <button
                key={option.value || "todas"}
                ref={(element) => {
                  pillRefs.current[index] = element;
                }}
                type="button"
                role="radio"
                aria-checked={selected}
                tabIndex={selected ? 0 : -1}
                className={`members-filters__pill${selected ? " members-filters__pill--selected" : ""}`}
                onClick={() => onCategoryChange(option.value)}
                onKeyDown={(event) => handleKeyDown(event, index)}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
